import { useRef, useState } from 'preact/hooks';
import { EDGE_ZONE } from './useEdgeSwipeBack.js';

/** Сколько нужно протянуть вправо, чтобы жест цитирования засчитался. */
const THRESHOLD = 56;
/** Дальше пузырь не едет — иначе жест выглядит как перетаскивание. */
const MAX_SHIFT = 72;
/** Ниже этого горизонтальный сдвиг считается дрожанием пальца, а не жестом. */
const MIN_SHIFT = 8;
/** Промежуток между касаниями, в пределах которого это ещё двойное нажатие. */
const DOUBLE_TAP_MS = 320;
/** Насколько палец может сместиться, чтобы касание всё ещё считалось нажатием. */
const TAP_SLOP = 12;

/**
 * Жесты на сообщении: свайп вправо — цитирование, двойное нажатие — меню.
 *
 * Меню здесь не украшение: кнопки над пузырём показываются только по
 * наведению мыши, а на телефоне наведения не существует. Без него правка
 * сообщений с телефона была бы недоступна вовсе.
 *
 * Оба жеста живут в одном обработчике намеренно: они делят одни и те же
 * события касания, и разнеси их по разным хукам — они начали бы отменять
 * друг друга.
 */
export function useMessageGestures(onReply: () => void, onMenu: () => void) {
  const [shift, setShift] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const direction = useRef<'unknown' | 'horizontal' | 'vertical'>('unknown');
  /** Палец уезжал за порог — значит это прокрутка или свайп, но не нажатие. */
  const moved = useRef(false);
  const lastTap = useRef<{ time: number; x: number; y: number } | null>(null);

  /**
   * Сдвиг дублируется в ref: состояние обновляется не сразу, и обработчик
   * завершения жеста, читая только его, видел бы значение до последнего
   * движения пальца.
   */
  const current = useRef(0);
  const move = (value: number) => {
    current.current = value;
    setShift(value);
  };

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;

    // У самого края экрана начинается жест возврата к списку чатов.
    // Не перехватываем: иначе два жеста поехали бы одновременно.
    if (touch.clientX <= EDGE_ZONE) {
      start.current = null;
      return;
    }

    start.current = { x: touch.clientX, y: touch.clientY };
    direction.current = 'unknown';
    moved.current = false;
  };

  const onTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch || !start.current) return;

    const dx = touch.clientX - start.current.x;
    const dy = touch.clientY - start.current.y;

    if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) moved.current = true;

    if (direction.current === 'unknown') {
      if (Math.abs(dx) < MIN_SHIFT && Math.abs(dy) < MIN_SHIFT) return;
      direction.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }
    if (direction.current === 'vertical') return;

    // Только вправо: свайп влево ничего не значит и не должен тянуть пузырь.
    if (dx <= 0) {
      move(0);
      return;
    }
    // Сопротивление у края: чем дальше тянешь, тем медленнее едет —
    // так чувствуется предел, и жест не превращается в перетаскивание.
    move(Math.min(MAX_SHIFT, dx * (dx > THRESHOLD ? 0.35 : 1)));
  };

  const onTouchEnd = (event: TouchEvent) => {
    const swiped = current.current >= THRESHOLD * 0.6;

    move(0);
    start.current = null;
    direction.current = 'unknown';

    if (swiped) {
      onReply();
      // Короткая отдача, если устройство умеет: подтверждение жеста на ощупь.
      navigator.vibrate?.(10);
      lastTap.current = null;
      return;
    }

    // Прокрутка ленты не должна засчитываться как первое нажатие пары,
    // иначе меню открывалось бы от случайного касания после скролла.
    if (moved.current) {
      lastTap.current = null;
      return;
    }

    const touch = event.changedTouches[0];
    if (!touch) return;

    const now = Date.now();
    const previous = lastTap.current;
    const nearby =
      previous !== null &&
      Math.abs(touch.clientX - previous.x) < TAP_SLOP * 2 &&
      Math.abs(touch.clientY - previous.y) < TAP_SLOP * 2;

    if (previous && nearby && now - previous.time < DOUBLE_TAP_MS) {
      lastTap.current = null;
      onMenu();
      navigator.vibrate?.(15);
      return;
    }

    lastTap.current = { time: now, x: touch.clientX, y: touch.clientY };
  };

  const onTouchCancel = () => {
    move(0);
    start.current = null;
    direction.current = 'unknown';
    lastTap.current = null;
  };

  /**
   * Системное меню по долгому нажатию подавляем: пользователь целится
   * в двойное нажатие, а браузер успевает вклиниться со своим «скопировать».
   */
  const onContextMenu = (event: Event) => {
    if (matchMedia('(pointer: coarse)').matches) event.preventDefault();
  };

  /**
   * Запасной путь к меню.
   *
   * Замер по касаниям иногда не срабатывает: браузер может увести события
   * на собственную обработку двойного тапа и прислать только dblclick.
   * Открыть меню дважды не страшно — состояние выставляется то же самое.
   * На десктопе не вмешиваемся: там двойной щелчок выделяет слово, а меню
   * и так доступно кнопками по наведению.
   */
  const onDblClick = (event: Event) => {
    if (!matchMedia('(pointer: coarse)').matches) return;
    event.preventDefault();
    onMenu();
  };

  return {
    shift,
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, onContextMenu, onDblClick },
  };
}
